import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField

} from "@mui/material";

import { GlobalStyles } from "@mui/system";
import { IconButton } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../../AuthProvider";
import StudentTable from "./StudentTable";
import StudentFilters from "./StudentFilters";
import SchoolIcon from "@mui/icons-material/School";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

const ROMAN_CLASS_MAP = {
  NURSERY: "LKG",
  LKG: "UKG",
  UKG: "Class-I",
  "Class-I": "Class-II",
  "Class-II": "Class-III",
  "Class-III": "Class-IV",
  "Class-IV": "Class-V",
  "Class-V": "Class-VI",
  "Class-VI": "Class-VII",
  "Class-VII": "Class-VIII",
  "Class-VIII": "Class-IX",
  "Class-IX": "Class-X",
  "Inter-1st-Year": "Inter-2nd-Year",
};

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const StudentPromotion = () => {
  const { oId, board, pId, userId, role } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [originalData, setOriginalData] = useState([]); // Original API data
  const [loading, setLoading] = useState(true);
  const [sscClassOptions, setSscClassOptions] = useState([]);
  const [sscSectionOptions, setSscSectionOptions] = useState([]);
  const [sscSelectedClass, setSscSelectedClass] = useState("Class-I");
  const [sscSelectedSection, setSscSelectedSection] = useState("Section-A");
  const [formData, setFormData] = useState({});

  // INTERMEDIATE states
  const [interSectionOptions, setInterSectionOptions] = useState([]);
  const [interSelectedClass, setInterSelectedClass] =
    useState("Inter-1st-Year");
  const [interSelectedSection, setInterSelectedSection] = useState("Section-A");
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promoteTenthLoading, setPromoteTenthLoading] = useState(false);
  const [tenthPromotionDialogOpen, setTenthPromotionDialogOpen] = useState(false); // NEW
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState("");
  const UserId = userId || selectedUsers;
  const isPrincipal = useMemo(() => role?.startsWith("PRINCIPAL"), [role]);
  const [selectedPromotionClass, setSelectedPromotionClass] = useState("");
  const DEFAULT_ACADEMIC_YEAR = "2026-2027";
  const [selectedStudents, setSelectedStudents] = useState([]);
    const navigate = useNavigate();



  const fetchUsers = async () => {
    try {
      const response = await fetch(
        `${baseUrl}/api/principal/getAllUserIdsAndNames/ADMISSION_FEE/${oId}/${pId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const data = await response.json();
      setUsers(data);
      if (data.length > 0) {
        setSelectedUsers(data[0].userId); // ✅ Set first record's userId as default
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  useEffect(() => {
    if (isPrincipal) {
      fetchUsers();
    }
  }, []); // ✅ Only runs on mount

  // -------------------------------------------
  //           For SSC functions
  // -------------------------------------------

  // ---- Fetch Class + Sections for SSC (School) ----
  const fetchClassRegistrationData = async () => {
    try {
      const response = await fetch(
        `${baseUrl}/api/dynamic-classReg/class-registrations/${oId}/${pId}`,
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        const classes = data.map((item) => item.classRegistration);
        setSscClassOptions(classes);

        let defaultClass =
          sscSelectedClass ||
          (classes.includes("Class-I") ? "Class-I" : classes[0] || "");
        setSscSelectedClass(defaultClass);

        const defaultClassData = data.find(
          (item) => item.classRegistration === defaultClass,
        );
        if (defaultClassData) {
          const sections = defaultClassData.sections.split(",");
          setSscSectionOptions(sections);
          setSscSelectedSection(
            sections.includes("Section-A") ? "Section-A" : sections[0],
          );
        } else {
          setSscSectionOptions([]);
          setSscSelectedSection("");
        }
      }
    } catch (error) {
      console.error("Error fetching SSC data:", error);
    }
  };

  // ---- Fetch Table Data for SSC ----
  const fetchClassSectionTableData = async () => {
    if (!UserId || !sscSelectedClass || !sscSelectedSection) {
      setOriginalData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/api/admission/adm/getAllFilterWithClassesAndSection/${oId}/${UserId}?romanNumberClass=${sscSelectedClass}&sections=${sscSelectedSection}`,
      );
      const data = await response.json();
      if (Array.isArray(data)) {
        setOriginalData(data);

        // setUniqueCourseType([...new Set(data.map(item => item.course))]);
      } else {
        setOriginalData([]);
      }
    } catch (error) {
      console.error("Error fetching SSC table data:", error);
    } finally {
      setLoading(false);
    }
  };




  // -------------------------------------------
  //           For Intermediate functions
  // -------------------------------------------

  // ---- Fetch Courses for INTERMEDIATE ----
  const fetchCourseData = async () => {
    if (!interSelectedClass) return;
    try {
      const response = await fetch(
        `${baseUrl}/api/dynamic-classReg/getAllcoursesAccordingClasses/${pId}?romanNumberClass=${interSelectedClass}`,
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        setCourseOptions(data);
        if (!selectedCourseType) {
          setSelectedCourseType(data[0] || "");
        }
      } else {
        setCourseOptions([]);
      }
    } catch (error) {
      console.error("Error fetching course data:", error);
      setCourseOptions([]);
    }
  };

  // ---- Fetch Sections for INTERMEDIATE ----
  const fetchSectionsData = async () => {
    if (!selectedCourseType) {
      setInterSectionOptions([]);
      return;
    }
    try {
      const response = await fetch(
        `${baseUrl}/api/dynamic-classReg/getAllClass-registrations/${interSelectedClass}/${selectedCourseType}/${pId}`,
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data) && data.length > 0) {
        const sectionsString = data[0].sections; // because your data is like [{ sections: "Section-A,Section-B" }]
        const sections = sectionsString
          .split(",")
          .map((section) => section.trim());

        setInterSectionOptions(sections);

        if (sections.length > 0) {
          setInterSelectedSection(sections[0]);
        } else {
          setInterSelectedSection("");
        }
      } else {
        setInterSectionOptions([]);
        setInterSelectedSection("");
      }
    } catch (error) {
      console.error("Error fetching INTERMEDIATE data:", error);
    }
  };

  // ---- Fetch Table Data for INTERMEDIATE ----
  const fetchCourseClassSectionTableData = async () => {
    if (
      !UserId ||
      !selectedCourseType ||
      !interSelectedClass ||
      !interSelectedSection
    ) {
      setOriginalData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/api/admission/adm/getAllFilterWithClassesAndSectionOnlyStudents/${oId}/${UserId}?course=${selectedCourseType}&romanNumberClass=${interSelectedClass}&sections=${interSelectedSection}`,
      );
      const data = await response.json();
      if (Array.isArray(data)) {
        setOriginalData(data);

        // setUniqueCourseType([...new Set(data.map(item => item.course))]);
      } else {
        setOriginalData([]);
      }
    } catch (error) {
      console.error("Error fetching INTERMEDIATE table data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (board === "SSC") {
      fetchClassRegistrationData();
    }
  }, [board]);

  // INTERMEDIATE: When class or course type changes, update sections
  useEffect(() => {
    const updateIntermediateSections = async () => {
      if (
        board === "INTERMEDIATE" &&
        interSelectedClass &&
        selectedCourseType
      ) {
        await fetchSectionsData();
      }
    };
    updateIntermediateSections();
  }, [interSelectedClass, selectedCourseType, board]);

  // Fetch course options if board is INTERMEDIATE
  useEffect(() => {
    if (board === "INTERMEDIATE") {
      fetchCourseData();
    }
  }, [interSelectedClass, board, oId]);

  // Fetch Table Data whenever filters change
  useEffect(() => {
    if (board === "SSC") {
      if (!sscSelectedClass || !sscSelectedSection) return;
      fetchClassSectionTableData(); // Modify inside function to use sscSelectedClass and sscSelectedSection
    } else if (board === "INTERMEDIATE") {
      if (!interSelectedClass || !interSelectedSection || !selectedCourseType)
        return;
      fetchCourseClassSectionTableData(); // Modify inside function to use interSelectedClass and interSelectedSection
    }
  }, [
    UserId,
    board,
    sscSelectedClass,
    sscSelectedSection,
    interSelectedClass,
    interSelectedSection,
    selectedCourseType,
  ]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return originalData;

    const term = searchQuery.toLowerCase();

    return originalData.filter((student) =>
      [
        student.generatedAdmissionNumber,
        student.surName,
        student.firstName,
        student.lastName,
        student.primaryContact,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [searchQuery, originalData]);
  useEffect(() => {
    setSelectedStudents([]);
  }, [filteredData]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const promotionFromClass =
    board === "SSC" ? sscSelectedClass : interSelectedClass;

  const promotionToClass = ROMAN_CLASS_MAP[promotionFromClass] || "";

  // whether the 10th class ("Class-X") is currently selected on the SSC board
  const isTenthClassSelected = board === "SSC" && sscSelectedClass === "Class-X";

  const handleSave = async () => {
    if (!formData.studentId) {
      toast.error("Admission number is missing.", { autoClose: 2000 });
      return;
    }

    // Create a FormData object and append the required fields
    const updatedFormData = new FormData();
    updatedFormData.append("surName", formData.surName || "");
    updatedFormData.append("firstName", formData.firstName || "");
    updatedFormData.append("lastName", formData.lastName || "");
    updatedFormData.append("primaryContact", formData.primaryContact || "");
    updatedFormData.append("sections", formData.sections || "");
    // updatedFormData.append("admissionFee", formData.admissionFee || "");
    updatedFormData.append("tutionFee", formData.tutionFee || "");

    try {
      const response = await fetch(
        `${baseUrl}/api/studentPromotion/promote/${formData.romanNumberClass}/${formData.studentId}/${oId}/${pId}`,
        {
          method: "PUT",
          body: updatedFormData, // Send FormData instead of JSON
        },
      );

      if (response.ok) {
        toast.success("Student Promoted successfully!", { autoClose: 2000 });

        if (board === "INTERMEDIATE") {
          fetchCourseClassSectionTableData();
        } else if (board === "SSC") {
          fetchClassSectionTableData();
        }
      } else {
        toast.error("Failed to update student details.", { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Error updating student details:", error);
      toast.error("An error occurred while updating student details.", {
        autoClose: 2000,
      });
    }
  };

  const handlePromoteStudents = async () => {
    if (!selectedPromotionClass) {
      toast.error("Next class not available");
      return;
    }

    try {
      setPromotionLoading(true);

      const response = await fetch(
        `${baseUrl}/api/studentPromotion/${promotionFromClass}/${selectedPromotionClass}/selected?newAcademicYear=${DEFAULT_ACADEMIC_YEAR}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(selectedStudents)
        }
      );

      const message = await response.text();

      if (!response.ok) {
        throw new Error(message || "Promotion failed");
      }

      toast.success(
        `Students promoted successfully from ${promotionFromClass} to ${selectedPromotionClass}`,
        {
          autoClose: 2000,
        },
      );

      setPromotionDialogOpen(false);
      setSelectedStudents([]);

      if (board === "SSC") {
        fetchClassSectionTableData();
      } else {
        fetchCourseClassSectionTableData();
      }
    } catch (error) {
      console.error(error);

      toast.error(error.message || "Failed to promote students", {
        autoClose: 3000,
      });
    } finally {
      setPromotionLoading(false);
    }
  };

  // Opens the confirmation dialog for 10th class promotion — does NOT call the API yet
  const openTenthPromotionDialog = () => {
    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student to promote.", {
        autoClose: 2000,
      });
      return;
    }
    setTenthPromotionDialogOpen(true);
  };

  // Actual POST call — fires only when user clicks "Confirm Promotion" in the dialog
  // Hits: POST /class10-to-class11?newAcademicYear=... with body = List<Long> admissionNumbers
  const handlePromoteTenthStudents = async () => {
    try {
      setPromoteTenthLoading(true);

      // TODO: prefix with your actual base path if this controller is mounted
      // under something other than /api/studentPromotion
      const response = await fetch(
        `${baseUrl}/api/studentPromotion/class10-to-class11?newAcademicYear=${DEFAULT_ACADEMIC_YEAR}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selectedStudents), // List<Long> admissionNumbers
        },
      );

      const message = await response.text();

      if (!response.ok) {
        throw new Error(message || "Promotion failed");
      }

      toast.success("10th class students promoted successfully!", {
        autoClose: 2000,
        navigate: "OldTenthStudents", // Optional: navigate to a different page after success
      });

      setTenthPromotionDialogOpen(false);
      setSelectedStudents([]);
      fetchClassSectionTableData();
      setTimeout(() => {
        navigate("/OldTenthStudents"); // update to your actual route path
      }, 2000);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to promote 10th class students", {
        autoClose: 2000,
      });
    } finally {
      setPromoteTenthLoading(false);
    }
  };

  const openPromotionDialog = () => {
    setSelectedPromotionClass(
      ROMAN_CLASS_MAP[promotionFromClass] || ""
    );
    setPromotionDialogOpen(true);

  };

  const promotionClassOptions = useMemo(() => {
    return sscClassOptions.filter(
      (cls) => cls !== promotionFromClass
    );
  }, [sscClassOptions, promotionFromClass]);
  useEffect(() => {
    console.log("Selected Students:", selectedStudents);
  }, [selectedStudents]);

  return (
    <Box sx={{ mt: "5%", mr: "1%", ml: "1%" }}>
      <Card
        sx={{
          marginTop: "60px",
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >

        <CardContent>

          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Promoted Student List
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    mt: 0.5,
                  }}
                >
                  Manage and promote students to the next academic class.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<SchoolIcon />}
                disabled={
                  filteredData.length === 0 ||
                  selectedStudents.length === 0
                }
                onClick={openPromotionDialog}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #2e7d32, #43a047)",
                  boxShadow: "0 6px 18px rgba(46,125,50,0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 24px rgba(46,125,50,0.4)",
                    background: "linear-gradient(135deg, #1b5e20, #388e3c)",
                  },
                }}
              >
                Promote Students
              </Button>
            </Box>

            {/* Instructions Card */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(66,165,245,0.12))",
                borderLeft: "5px solid #1976d2",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1565c0",
                  mb: 1,
                }}
              >
                📌 Promotion Instructions
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#374151",
                  lineHeight: 1.8,
                }}
              >
                • Select the correct class before proceeding. Verify the student list carefully to ensure all students belong to
                the selected class
                & Click <strong>Promote Students</strong> only after reviewing the
                displayed records.
                <br />
                • Students will automatically be moved to the next academic class for
                the academic year <strong>{DEFAULT_ACADEMIC_YEAR}</strong>.
                <br />
                • This action is permanent and cannot be undone after confirmation.
              </Typography>
            </Box>
          </Box>
          <StudentFilters
            board={board}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sscClassOptions={sscClassOptions}
            sscSelectedClass={sscSelectedClass}
            setSscSelectedClass={setSscSelectedClass}
            sscSectionOptions={sscSectionOptions}
            sscSelectedSection={sscSelectedSection}
            setSscSelectedSection={setSscSelectedSection}
            interSelectedClass={interSelectedClass}
            setInterSelectedClass={setInterSelectedClass}
            selectedCourseType={selectedCourseType}
            setSelectedCourseType={setSelectedCourseType}
            courseOptions={courseOptions}
            interSelectedSection={interSelectedSection}
            setInterSelectedSection={setInterSelectedSection}
            interSectionOptions={interSectionOptions}
            isPrincipal={isPrincipal}
            users={users}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            rightAction={
              isTenthClassSelected ? (
                <Button
                  variant="contained"
                  startIcon={<SchoolIcon />}
                  disabled={selectedStudents.length === 0}
                  onClick={openTenthPromotionDialog}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #ef6c00, #f9a825)",
                    boxShadow: "0 6px 18px rgba(239,108,0,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 24px rgba(239,108,0,0.4)",
                      background: "linear-gradient(135deg, #e65100, #f57f17)",
                    },
                  }}
                >
                  Promote 10th Students
                </Button>
              ) : null
            }
          />

          <GlobalStyles
            styles={{
              ".custom-sidebar-scrollbar": {
                scrollbarWidth: "thin", // For Firefox
                scrollbarColor: "grey #e0e0e0",
              },
              ".custom-sidebar-scrollbar::-webkit-scrollbar": {
                width: "6px", // For WebKit-based browsers
              },
              ".custom-sidebar-scrollbar::-webkit-scrollbar-track": {
                background: "#f1f1f1", // Track color
              },
              ".custom-sidebar-scrollbar::-webkit-scrollbar-thumb": {
                backgroundColor: "grey", // Thumb color
                borderRadius: "10px",
              },
              ".custom-sidebar-scrollbar::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555",
              },
            }}
          />


          <StudentTable
            loading={loading}
            filteredData={filteredData}
            board={board}
            selectedStudents={selectedStudents}
            setSelectedStudents={setSelectedStudents}
          />

          {/* Existing "Promote Students" dialog (regular promotion) */}
          <Dialog
            open={promotionDialogOpen}
            onClose={() => setPromotionDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                overflow: "hidden",
              },
            }}
          >
            {/* Header */}
            <DialogTitle
              sx={{
                background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 2,
              }}
            >
              <SchoolIcon />
              <Typography variant="h6" fontWeight={700}>
                Student Promotion Confirmation
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  color: "text.secondary",
                }}
              >
                You are about to promote all students to the next class. Please
                verify the details before proceeding.
              </Typography>

              {/* Class Details Card */}
              <Box
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e0e0e0",
                  p: 3,
                  bgcolor: "#fafafa",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                >
                  {/* From Class */}
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 2,
                      bgcolor: "#fff3e0",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      FROM CLASS
                    </Typography>

                    <Typography variant="h6" fontWeight={700} color="#ef6c00">
                      {promotionFromClass}
                    </Typography>
                  </Box>

                  <ArrowForwardIcon
                    sx={{
                      fontSize: 40,
                      color: "#1976d2",
                    }}
                  />

                  {/* To Class */}
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 2,
                      bgcolor: "#e8f5e9",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      TO CLASS
                    </Typography>

                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={selectedPromotionClass}
                      onChange={(e) => setSelectedPromotionClass(e.target.value)}
                    >
                      {promotionClassOptions.map((cls) => (
                        <MenuItem key={cls} value={cls}>
                          {cls}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                {/* Details */}
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Academic Year
                    </Typography>

                    <Typography fontWeight={600}>
                      {DEFAULT_ACADEMIC_YEAR}
                    </Typography>
                  </Box>

                  {/* <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Students
                    </Typography>

                    <Typography fontWeight={600}>
                      {filteredData.length}
                    </Typography>
                  </Box> */}
                </Box>
              </Box>

              {/* Warning */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff8e1",
                  border: "1px solid #ffe082",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <WarningAmberIcon color="warning" />

                <Typography variant="body2" color="text.secondary">
                  This action will promote all students from{" "}
                  <strong>{promotionFromClass}</strong> to{" "}
                  <strong>{selectedPromotionClass}</strong>.
                  <br />
                  This action cannot be undone.
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setPromotionDialogOpen(false)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={handlePromoteStudents}
                disabled={promotionLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 4,
                  fontWeight: 700,
                  boxShadow: 3,
                }}
              >
                {promotionLoading ? "Promoting..." : "Confirm Promotion"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={tenthPromotionDialogOpen}
            onClose={() => !promoteTenthLoading && setTenthPromotionDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                overflow: "hidden",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(135deg, #ef6c00, #f9a825)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 2,
              }}
            >
              <SchoolIcon />
              <Typography variant="h6" fontWeight={700}>
                Promote 10th Class Students
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  color: "text.secondary",
                }}
              >
                You are about to promote the selected 10th class students. Please
                verify the details before proceeding.
              </Typography>

              <Box
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e0e0e0",
                  p: 3,
                  bgcolor: "#fafafa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Academic Year
                    </Typography>
                    <Typography fontWeight={600}>
                      {DEFAULT_ACADEMIC_YEAR}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Students Selected
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedStudents.length}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff8e1",
                  border: "1px solid #ffe082",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <WarningAmberIcon color="warning" />
                <Typography variant="body2" color="text.secondary">
                  This action will promote <strong>{selectedStudents.length}</strong>{" "}
                  selected student(s) for the{" "}
                  <strong>{DEFAULT_ACADEMIC_YEAR}</strong> academic year.
                  <br />
                  This action cannot be undone.
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setTenthPromotionDialogOpen(false)}
                disabled={promoteTenthLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={handlePromoteTenthStudents}
                disabled={promoteTenthLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 4,
                  fontWeight: 700,
                  boxShadow: 3,
                }}
              >
                {promoteTenthLoading ? "Promoting..." : "Confirm Promotion"}
              </Button>
            </DialogActions>
          </Dialog>

          <ToastContainer position="top-right" autoClose={2000} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentPromotion;